import type {
  AnchorHTMLAttributes,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
  RefCallback,
} from 'react';

/** Where a header control rendered: the bar itself, the desktop account menu, or the mobile drawer. */
export type HeaderPlacement = 'bar' | 'menu' | 'drawer';

/** Anchor props the header passes to an app-router link. */
export type HeaderLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string;
  children?: ReactNode;
};

/**
 * Renders client-side navigation for `navigation: 'app'` links. It must render one anchor and
 * forward every prop, event handler, and the ref to it. Define it at module scope: a new
 * component on each render remounts every link.
 */
export type HeaderLinkComponent = ForwardRefExoticComponent<
  HeaderLinkProps & RefAttributes<HTMLAnchorElement>
>;

export type HeaderLinkAction = {
  kind: 'link';
  /** A function lets the destination record which surface the customer used. */
  href: string | ((placement: HeaderPlacement) => string);
  /**
   * `document` renders a native anchor for routes another app owns, so the browser loads a new
   * document. `app` renders the adapter's router link.
   */
  navigation: 'document' | 'app';
  /** Runs synchronously in the click handler, before navigation starts. */
  onSelect?: (placement: HeaderPlacement) => void;
};

export type HeaderCommandAction = {
  kind: 'command';
  run: () => void;
};

/** A control that stays visible but cannot run, such as sign-out in a read-only preview. */
export type HeaderUnavailableAction = {
  kind: 'unavailable';
  /** Shown under the control's label. */
  reason: string;
};

export type HeaderRetryAction = {
  run: (placement: HeaderPlacement) => void;
};

type HeaderNavLink = {
  key: string;
  label: string;
  action: HeaderLinkAction;
  /** Marks the link with `aria-current="page"`. */
  current?: boolean;
};

export type HeaderNavItem =
  | ({ kind: 'link' } & HeaderNavLink)
  | {
      kind: 'group';
      key: string;
      label: string;
      links: HeaderNavLink[];
    };

export type HeaderIdentity = {
  /** Stable per person. A new key remounts the account menu so a badge never outlives its owner. */
  key: string;
  fullName: string;
  firstName: string;
  email: string;
  badge: 'agent' | null;
};

export type HeaderTransactionRow = {
  key: string;
  label: string;
  icon: 'purchase' | 'sale';
  action: HeaderLinkAction;
};

type HeaderMenuLink = {
  key: string;
  label: string;
  action: HeaderLinkAction;
};

export type HeaderAccountMenu = {
  /** Shown beside the avatar on desktop and as the first drawer row. */
  dashboard: { action: HeaderLinkAction; current: boolean };
  /** In the adapter's business order. */
  transactions: HeaderTransactionRow[];
  /** Lists every transaction beyond the capped desktop menu. */
  moreTransactions: HeaderLinkAction;
  /** App-specific rows after the transactions, such as "Closing dash". */
  transactionLinks: HeaderMenuLink[];
  account: HeaderLinkAction;
  helpCenter: HeaderLinkAction;
  signOut: HeaderCommandAction | HeaderUnavailableAction;
  /** Runs when the avatar trigger is pressed, open or closed. */
  onTriggerPress?: () => void;
};

export type HeaderSignIn = {
  action: HeaderLinkAction;
  /**
   * Receives the bar's sign-in anchor, for apps that observe its visibility. A callback sees the
   * anchor mount after the session resolves.
   */
  barRef?: RefCallback<HTMLAnchorElement>;
};

export type HeaderAccountState =
  | { status: 'signedOut'; signIn: HeaderSignIn | null }
  | { status: 'loading' }
  | {
      status: 'error';
      /** Null when another attempt cannot succeed. */
      retry: HeaderRetryAction | null;
      signIn: HeaderSignIn | null;
    }
  | { status: 'authenticated'; identity: HeaderIdentity; menu: HeaderAccountMenu };

/**
 * `solid` and `solid-secondary` paint the primary or secondary surface. `transparent` lets the
 * page show through with dark content. `inverse` uses light content over a dark hero.
 */
export type HeaderAppearance = 'solid' | 'solid-secondary' | 'transparent' | 'inverse';

export type HeaderDrawerCallToAction = {
  title: string;
  label: string;
  action: HeaderLinkAction;
};

// Every header overlay shares one layer, above each app's sticky header wrapper.
export const HEADER_OVERLAY_LAYER = 'z-[3000]';

// Keeps the desktop menu within one screen; the drawer lists every row.
export const MAX_MENU_TRANSACTIONS = 3;

/**
 * The header swaps the drawer for the desktop nav at this width in every app, because the apps'
 * own `lg` breakpoints differ. `header.css` defines the matching `site-header-desktop` variant.
 */
export const HEADER_DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

function nonBlank(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

// Array.from iterates code points, so an astral character survives instead of a lone surrogate.
function firstCodePoint(value: string): string {
  return Array.from(value)[0] ?? '';
}

export function headerInitials(identity: Pick<HeaderIdentity, 'fullName' | 'firstName' | 'email'>) {
  const source =
    nonBlank(identity.fullName) ?? nonBlank(identity.firstName) ?? nonBlank(identity.email);
  if (!source) return '';
  const parts = source.split(/\s+/);
  const first = firstCodePoint(parts[0] ?? '');
  const last = parts.length > 1 ? firstCodePoint(parts[parts.length - 1] ?? '') : '';
  return (first + last).toUpperCase();
}

export function headerDisplayName(identity: Pick<HeaderIdentity, 'fullName' | 'firstName'>) {
  return identity.fullName.trim() || identity.firstName.trim();
}
